import React, { useMemo, useState, useEffect } from "react";
import Calendar, { CalendarProps } from "react-calendar";
import { OnArgs } from "react-calendar/dist/shared/types";
import "react-calendar/dist/Calendar.css";
import "@/assets/css/calendar-fluent.css";
import {
  ChevronDoubleLeft16Regular,
  ChevronDoubleRight16Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
} from "../icons/IconCustom";
import Button from "./Button";
import { useSettingStore } from "../../shared/stores";

interface IFluentCalendarProps extends CalendarProps {
  className?: string;
  isDarkMode?: boolean;
  highlightedDates?: Date[]; // List các ngày cần bôi đậm
}

const FluentCalendar: React.FC<IFluentCalendarProps> = (props: IFluentCalendarProps) => {
  const { highlightedDates, ...calendarProps } = props;
  const { firstDayOfWeek } = useSettingStore();

  const formatShortWeekday = useMemo(() => {
    const defaultWeekdayLabels = ["S", "M", "T", "W", "T", "F", "S"];
    if (firstDayOfWeek === -1) {
      return defaultWeekdayLabels;
    }
    const weekdayLabels = defaultWeekdayLabels
      .slice(firstDayOfWeek)
      .concat(defaultWeekdayLabels.slice(0, firstDayOfWeek));
    return weekdayLabels;
  }, [firstDayOfWeek]);

  // State để control activeStartDate (tháng hiện tại đang hiển thị)
  const [activeStartDate, setActiveStartDate] = useState<Date | undefined>(calendarProps.activeStartDate || new Date());

  // Sync với prop activeStartDate từ bên ngoài
  useEffect(() => {
    if (calendarProps.activeStartDate) {
      setActiveStartDate(calendarProps.activeStartDate);
    }
  }, [calendarProps.activeStartDate]);

  // Function để check ngày có trong list cần highlight không
  const isSameDate = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Function để thêm class cho các ngày cần highlight
  const getTileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (highlightedDates && highlightedDates.length > 0) {
      const isHighlighted = highlightedDates.some((highlightDate) => isSameDate(date, highlightDate));
      if (isHighlighted) {
        return "font-semibold-date";
      }
    }
    return "";
  };

  const isDisabledToday = useMemo(() => {
    if (!calendarProps.value) return false;
    const today = new Date();
    const calendarValue = calendarProps.value as Date;
    if (calendarValue.getFullYear() === today.getFullYear() && calendarValue.getMonth() === today.getMonth()) {
      return true;
    }
    return false;
  }, [calendarProps.value]);

  const onTodayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const today = new Date();

    // Navigate calendar về tháng hiện tại
    setActiveStartDate(today);

    // Gọi callback onActiveStartDateChange nếu có
    if (calendarProps.onActiveStartDateChange) {
      // Convert value to proper type (Date | null or Range)
      let value: Date | null | [Date | null, Date | null] = today;
      if (calendarProps.value) {
        if (calendarProps.value instanceof Date) {
          value = calendarProps.value;
        } else if (Array.isArray(calendarProps.value)) {
          value = calendarProps.value as [Date | null, Date | null];
        } else {
          value = today;
        }
      }

      const args: OnArgs = {
        activeStartDate: today,
        view: "month",
        action: "onChange",
        value: value,
      };
      calendarProps.onActiveStartDateChange(args);
    }

    // Chọn ngày hôm nay
    if (calendarProps.onClickDay) {
      calendarProps.onClickDay(today, e as React.MouseEvent<HTMLButtonElement>);
    }
  };

  const handleActiveStartDateChange = (args: OnArgs) => {
    if (args.activeStartDate) {
      setActiveStartDate(args.activeStartDate);
    }

    // Gọi callback từ props nếu có
    if (calendarProps.onActiveStartDateChange) {
      calendarProps.onActiveStartDateChange(args);
    }
  };

  return (
    <div className="flex flex-col justify-center">
      <Calendar
        {...calendarProps}
        activeStartDate={activeStartDate}
        onActiveStartDateChange={handleActiveStartDateChange}
        locale="en-US"
        formatShortWeekday={(_locale, date) => formatShortWeekday[date.getDay()]}
        showNeighboringMonth={false}
        minDetail="month"
        prev2Label={<ChevronDoubleLeft16Regular isDarkMode={props.isDarkMode} />}
        prevLabel={<ChevronLeft16Regular isDarkMode={props.isDarkMode} />}
        nextLabel={<ChevronRight16Regular isDarkMode={props.isDarkMode} />}
        next2Label={<ChevronDoubleRight16Regular isDarkMode={props.isDarkMode} />}
        className={props.className}
        tileClassName={getTileClassName}
      />
      <Button
        disabled={isDisabledToday}
        onClick={onTodayClick}
        label="Today"
        className="mt-1 text-xs font-semibold text-primaryBlue dark:text-brightBlue"
      />
    </div>
  );
};

export default FluentCalendar;
